/**
 * Cliente HTTP tipado para la API REST del backend (Workers).
 *
 * Comportamiento:
 * - credentials: 'include' para enviar la cookie __Secure-Session HttpOnly.
 * - Parsea el JSON de respuesta con el schema Zod provisto (lanza si no matchea).
 * - Errores 4xx/5xx se mapean a ApiError con code (RFC códigos estándar).
 * - Soporta `responseType: 'blob'` para descargas (PDF, CSV).
 */
import { z } from 'zod';
import { ApiErrorBodySchema, type ErrorCode } from '@formacion-docente/schemas';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: ErrorCode,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type Init = RequestInit & { responseType?: 'json' | 'blob' | 'text' };

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

export async function apiFetch<T>(
  path: string,
  schema: z.ZodType<T>,
  init: Init = {},
): Promise<T> {
  const { responseType = 'json', headers, ...rest } = init;
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
  const response = await fetch(url, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: responseType === 'blob' ? 'application/octet-stream' : 'application/json',
      ...headers,
    },
    ...rest,
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => null);
    const parsed = ApiErrorBodySchema.safeParse(errorBody);
    if (parsed.success) {
      throw new ApiError(
        response.status,
        parsed.data.error.code,
        parsed.data.error.message,
        parsed.data.error.details,
      );
    }
    throw new ApiError(response.status, 'internal_error', `HTTP ${response.status}`);
  }

  if (responseType === 'blob') {
    const blob = (await response.blob()) as unknown as T;
    return blob;
  }

  // 204 No Content o body vacío: schema.unknown() acepta undefined.
  const text = await response.text();
  if (!text) {
    return schema.parse(undefined);
  }
  const data = JSON.parse(text);
  return schema.parse(data);
}